import { create } from "zustand";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type SidebarNavItem =
  | {
      type: "link";
      id: string;
      label: string;
      icon?: LucideIcon;
      href: string;
      count?: number;
      badge?: string;
      /** When true, only exact pathname match will be considered active (no prefix matching) */
      exact?: boolean;
    }
  | {
      type: "section";
      id: string;
      label: string;
      icon?: LucideIcon;
      children: SidebarNavItem[];
      defaultExpanded?: boolean;
    }
  | {
      /**
       * A plan: tasks in the order they will be done, with the divisions between
       * them drawn where the work divides. Rendered as a list rather than a tree
       * because that is what the underlying shape is — see stage-nav-model.
       */
      type: "stages";
      id: string;
      items: StageNavItem[];
    }
  | {
      type: "divider";
      id: string;
    }
  | {
      type: "custom";
      id: string;
      content: ReactNode;
    };

/**
 * One line of a plan. A stage is a destination; a fork is punctuation between
 * destinations, and deliberately not clickable — everything the sidebar offers
 * to click is somewhere to go.
 */
export type StageNavItem =
  | {
      kind: "stage";
      /** The path this row navigates to. */
      href: string;
      label: string;
      /** Glyph key from the operation template. */
      icon: string | null;
      /** Streams the step runs on, or null when the session has only one. */
      streams: number | null;
    }
  | {
      kind: "fork";
      key: string;
      arms: string[];
      multiplicity: number;
    };

/**
 * Sidebar variant determines the expanded width:
 * - 'simple': 160px - for default/basic navigation
 * - 'complex': 220px - for tree navigation, rich contextual data
 */
export type SidebarVariant = "simple" | "complex";

export interface SidebarConfig {
  layer: 0 | 1 | 2;
  parent: {
    label: string;
    icon?: LucideIcon;
    href: string;
    /** Search params the back link carries, e.g. a mode or tab to land on. */
    search?: Record<string, string>;
  } | null;
  header?: {
    title: string;
    subtitle?: string;
    status?: ReactNode;
    meta?: ReactNode;
    actions?: ReactNode;
  } | null;
  customHeader?: ReactNode;
  /**
   * A strip between the header and the navigation, for something the operator
   * must be able to see from every page of the entity — live readings, not
   * controls. Absent on most entities.
   */
  readout?: ReactNode;
  navigation: SidebarNavItem[];
  footer?: ReactNode;
  /** Controls sidebar width. Defaults to 'simple' (160px). Use 'complex' (220px) for tree nav or rich data. */
  variant?: SidebarVariant;
}

interface SidebarStore {
  /**
   * Focus mode: the view has claimed the full width (via useFocusMode). The
   * rail stays docked; the secondary sidebar leaves the flow and is only shown
   * as a temporary overlay. Nothing is persisted — leaving the view ends it.
   */
  focus: boolean;
  setFocus: (focus: boolean) => void;
  /**
   * Labels beside the rail icons, pinned. Home forces it on and focus mode
   * forces it off; in between it is whatever the latch was last clicked to.
   */
  railExpanded: boolean;
  setRailExpanded: (expanded: boolean) => void;
  /**
   * Labels shown transiently from the latch. Unlike the pinned state this
   * floats over the content rather than taking layout width, so a peek never
   * reflows the page.
   */
  railPeek: boolean;
  openRailPeek: () => void;
  scheduleRailPeekClose: () => void;
  cancelRailPeekClose: () => void;
  overlayOpen: boolean;
  setOverlayOpen: (open: boolean) => void;
  /** Open on hover over a trigger (rail button, journal title). */
  openOverlayOnHover: () => void;
  /**
   * The pointer left a hover trigger without reaching the panel: close after a
   * short grace period so crossing the gap into the panel still works.
   */
  scheduleOverlayClose: () => void;
  cancelOverlayClose: () => void;
  config: SidebarConfig | null;
  setConfig: (config: SidebarConfig | null) => void;
}

let closeTimer: ReturnType<typeof setTimeout> | null = null;
const clearCloseTimer = () => {
  if (closeTimer !== null) {
    clearTimeout(closeTimer);
    closeTimer = null;
  }
};

let peekTimer: ReturnType<typeof setTimeout> | null = null;
const clearPeekTimer = () => {
  if (peekTimer !== null) {
    clearTimeout(peekTimer);
    peekTimer = null;
  }
};

export const useSidebarStore = create<SidebarStore>((set) => ({
  focus: false,
  setFocus: (focus) => {
    clearCloseTimer();
    set({ focus, overlayOpen: false });
  },
  railExpanded: false,
  setRailExpanded: (railExpanded) => {
    clearPeekTimer();
    set({ railExpanded, railPeek: false });
  },
  railPeek: false,
  openRailPeek: () => {
    clearPeekTimer();
    set({ railPeek: true });
  },
  scheduleRailPeekClose: () => {
    clearPeekTimer();
    peekTimer = setTimeout(() => {
      peekTimer = null;
      set({ railPeek: false });
    }, 300);
  },
  cancelRailPeekClose: clearPeekTimer,
  overlayOpen: false,
  setOverlayOpen: (overlayOpen) => {
    clearCloseTimer();
    set({ overlayOpen });
  },
  openOverlayOnHover: () => {
    clearCloseTimer();
    set({ overlayOpen: true });
  },
  scheduleOverlayClose: () => {
    clearCloseTimer();
    closeTimer = setTimeout(() => {
      closeTimer = null;
      set({ overlayOpen: false });
    }, 300);
  },
  cancelOverlayClose: clearCloseTimer,
  config: null,
  setConfig: (config) => set({ config }),
}));
